import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthenticationService } from '../authentication.service';
import { Global } from '../classes/Global';
import { OffersParentComponent } from '../classes/OffersParentComponent';
import { GenresService } from '../genres.service';
import { InfoService } from '../info.service';
import { OffersService } from '../offers.service';
import { OwnListService } from '../own-list.service';
import { SeoService } from '../seo.service';
import { SettingsService } from '../settings.service';
import { ThemeService } from '../theme.service';
import { WishlistService } from '../wishlist.service';
import { Location } from "@angular/common";
import { UrlValidator } from '../helpers/UrlValidater';
import { Config } from '../classes/Config';
import { StringHelper } from '../helpers/StringHelper';
import { StorageHelper } from '../helpers/StorageHelper';
import { BlocklistService } from '../blocklist.service';

@Component({
  selector: 'app-actors',
  templateUrl: './actors.component.html',
  styleUrls: ['./actors.component.scss'],
  providers: [ OffersService, GenresService, InfoService,
    WishlistService, AuthenticationService, OwnListService ]
})
export class ActorsComponent extends OffersParentComponent implements OnInit {

  ids: string = "";
  personType: string = "";
  personName: string = "";
  canGoBack: boolean = false;
  filtersShown: boolean = false;

  constructor(
    private offersService: OffersService,
    private location: Location,
    protected route: ActivatedRoute,
    protected router: Router,
    protected genresService: GenresService,
    protected infoService: InfoService,
    protected wishlistService: WishlistService,
    protected blocklistService: BlocklistService,
    protected authenticationService: AuthenticationService,
    protected ownlistService: OwnListService,
    protected settingsService: SettingsService,
    protected themeService: ThemeService,
    protected seoService: SeoService,
  ) {
    super(genresService, router, route, authenticationService, infoService, wishlistService, blocklistService, ownlistService, settingsService, themeService, seoService);
  }

  ngOnInit() {

    UrlValidator.validateUrl(this.route, this.router, 'on-sale', this);
    this.canGoBack = history && history.state && history.state['navigationId'] && history.state['navigationId'] > 1;
    this.listType = Global.AZ;
    StorageHelper.getInstance().save(StorageHelper.keys.listType, this.listType);
    super.onInit();
    this.getInfoFromUrl().then(() => {

      this.loadGenreAndItems();

      this.seoService.setTitle(StringHelper.format(this.getString("castTitle"), this.personName));
      this.seoService.setMetaDescription(StringHelper.format(this.getString('castMetaDesc'), this.personName));
      this.seoService.setMetaKeywords(StringHelper.format(this.getString('castMetaKeywords'), this.personName));
      this.seoService.setOGUrl(Config.frontendUrl + this.router.url);
    });

    // brauchen wir nicht mehr, wir setzen noIndex direkt im SeoService, das klappt auch wenn der Request fehlschlägt
    // this.seoService.setNoIndex(true);
  }

  back() {
    this.location.back();
  }

  getPersonName(): string {

    //if it was already assigned, don't bother reassigning it
    if (this.personName !== "") {
      return this.personName;
    }

    let personName = [];
    if(!this.items || this.items.length == 0 || this.ids == "") {
      return "";
    }

    const idsArray = this.ids.split(',');

    if(this.personType == "actors") {
      const allActors: any[] = this.items.reduce((acc: any[], x) => {
        let newArray = acc;
        if(x.actors) {
          newArray = acc.concat(x.actors);
        }
        return newArray;
      }, []);
      for(let id of idsArray) {
        const actor = allActors.filter(x => x.id == id)[0];
        if(actor) {
          personName.push(actor.name);
        }
      }
    }
    else if(this.personType == "screenwriters") {
      const allWriters: any[] = this.items.reduce((acc, x) => {
        let newArray = acc;
        if(x.writers) {
          newArray = acc.concat(x.writers);
        }
        return newArray;
      }, []);
      for(let id of idsArray) {
        const writer = allWriters.filter(x => x.id == id)[0];
        if(writer) {
          personName.push(writer.name);
        }
      }
    }
    else if(this.personType == "directors") {
      const allDirectors: any[] = this.items.reduce((acc, x) => {
        let newArray = acc;
        if(x.directors) {
          newArray = acc.concat(x.directors);
        }
        return newArray;
      }, []);
      for(let id of idsArray) {
        const director = allDirectors.filter(x => x.id == id)[0];
        if(director) {
          personName.push(director.name);
        }
      }
    }

    this.personName = personName.join(', ');

    return this.personName;
  }

  getInfoFromUrl() {

    return new Promise<void>((resolve, reject) => {

      //Notifier
      const ngUnsubscribe: Subject<any> = new Subject();

      //Get the additional listType parameter
      this.route.params.pipe(takeUntil(ngUnsubscribe)).subscribe(params => {
        this.ids = params['ids'];

        const parts = this.router.url.split('/');
        let personTypeUrl: string = "";
        if(parts.length == 6) {//without slug
          personTypeUrl = parts[parts.length - 2];
        }
        else if (parts.length == 7) {//with slug
          personTypeUrl = parts[parts.length - 3];
        }
        switch(personTypeUrl) {
          case this.getString("actorsUrl"):
            this.personType = "actors";
            break;
          case this.getString("writersUrl"):
            this.personType = "screenwriters";
            break;
          case this.getString("directorsUrl"):
            this.personType = "directors";
            break;
        }

        //notifies that params have been received and route.params can be unsubscribed
        ngUnsubscribe.next();
        ngUnsubscribe.complete();

        resolve();
      });

    });

  }

  getOffers() {
    return this.offersService.getOffersByPerson(
      this.personType == "actors" ? this.ids : undefined,
      this.personType == "directors" ? this.ids : undefined,
      this.personType == "screenwriters" ? this.ids : undefined,
      Global.getStore(), Global.getCountry(), Global.getMediaType(), this.listType,
      this.formatMultiSelectionForServer(this.selectedGenres, this.genres),
      this.sortKey, this.offset, this.limit, this.selectedReleaseYear,
      this.formatMultiSelectionForServer(this.selectedPriceRanges, this.priceRanges),
      this.movieRatingsFilters, this.selectedRuntime,
      this.selectedFrenchTitlesOption ? this.selectedFrenchTitlesOption['type'] : this.getFrenchTitlesOptions()[0]['type']
    );
  }

  reload(newUrl: boolean, replaceUrl?: boolean) {
    let itemsPresentInCache = false;
    itemsPresentInCache = this.loadItemsFromCache();

    if (!itemsPresentInCache) {
      //Genres already loaded, load items only
      this.loadOffers();
    }
  }

  initSorting() {
    this.sortKey = Global.RELEASE_DATE;
  }

  getSortingValues() {
    return Global.getSortingValues(this.listType);
  }

  //We don't want saved filters to be used here

  //override
  getSavedGenres() {
    this.selectedGenres = [];
    return null;
  }

  //override
  getReleaseYear(): string {
    return "";
  }

  //override
  getPriceRange() {
    return null;
  }

  //override
  getMovieRatingsFilters() {
    return null;
  }

}
